import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useLocation } from 'react-router-dom';
import {
    getCurrentUserRole,
    getCurrentUserId,
    getCurrentUserOrgId
} from '../utils/authHelper';
import {formatDate} from '../utils/dateConverter';
import { getRevenueTargets, saveRevenueTargets } from '../services/OrganisationService';

import {
    getPublicHolidays,
    deletePublicHoliday,
    addPublicHoliday
} from "../services/PublicHolidayServices";
import CompanyClosures from "../components/CompanyClosures";
import NotificationRecipients from "../components/NotificationRecipients";

import {
    getMe,
    getAllUsers,
    deleteUser,
    changePassword,
    addUser,
    changeUserActiveState,
    updateProfile,
    updateUser,
} from "../services/UserService";

import {
    getAllWorkingTime,
    addWorkingTime,
    deleteWorkingTime
} from "../services/WorkingTimeService";

import api from '../services/api';
import { getActionTypes, getAppActions } from '../services/AppActionService';
import { extractApiError } from '../utils/apiError';
import '../styles/AppActions.css';
import '../styles/Settings.css';
import { useToast, useConfirm } from '../context/UIContext';
import { useTheme } from '../context/ThemeContext';
import DataExportPanel from './DataExport';

const ACTION_TYPE_LABELS = {
    USER_LOGIN:             'Benutzeranmeldung',
    USER_LOGOUT:            'Benutzerabmeldung',
    USER_CREATED:           'Benutzer erstellt',
    USER_UPDATED:           'Benutzer geändert',
    USER_DELETED:           'Benutzer gelöscht',
    PROJECT_CREATED:        'Projekt erstellt',
    PROJECT_UPDATED:        'Projekt geändert',
    PROJECT_DELETED:        'Projekt gelöscht',
    INVOICE_CREATED:        'Rechnung erstellt',
    INVOICE_UPDATED:        'Rechnung geändert',
    INVOICE_DELETED:        'Rechnung gelöscht',
    OFFER_CREATED:          'Angebot erstellt',
    OFFER_UPDATED:          'Angebot geändert',
    OFFER_DELETED:          'Angebot gelöscht',
    ORDER_CREATED:          'Auftrag erstellt',
    ORDER_UPDATED:          'Auftrag geändert',
    ORDER_DELETED:          'Auftrag gelöscht',
    HOLIDAY_REQUESTED:      'Urlaubsantrag gestellt',
    HOLIDAY_APPROVED:       'Urlaub genehmigt',
    HOLIDAY_REJECTED:       'Urlaub abgelehnt',
    HOLIDAY_DELETED:        'Urlaub gelöscht',
    TIME_RECORD_CREATED:    'Zeiterfassung erstellt',
    TIME_RECORD_UPDATED:    'Zeiterfassung geändert',
    TIME_RECORD_DELETED:    'Zeiterfassung gelöscht',
    SICK_DAY_CREATED:       'Krankmeldung erstellt',
    SICK_DAY_UPDATED:       'Krankmeldung geändert',
    SICK_DAY_DELETED:       'Krankmeldung gelöscht',
    PASSWORD_CHANGED:       'Passwort geändert',
    PROFILE_UPDATED:        'Profil geändert',
    CUSTOMER_CREATED:       'Kunde erstellt',
    CUSTOMER_UPDATED:       'Kunde geändert',
    CUSTOMER_DELETED:       'Kunde gelöscht',
};

function SortIcon({ active, order }) {
    if (!active) return <span className="aa-sort-icon">↕</span>;
    return <span className="aa-sort-icon">{order === 'asc' ? '↑' : '↓'}</span>;
}

function formatActionDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} ${time}`;
}

export default function Settings() {
    const toast = useToast();
    const confirm = useConfirm();
    const { colorScheme, setColorScheme, applyBranding } = useTheme();
    const location = useLocation();
    const [role, setRole] = useState('');
    const isAdmin = role === 'admin' || role === 'super_admin';
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({
        username: '',
        name: '',
        passwordClear: '',
        email: '',
        role: 'user'
    });
    const [ownPassword, setOwnPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [ownProfile, setOwnProfile] = useState({ username: '', email: '' });
    const [adminPasswordChanges, setAdminPasswordChanges] = useState({});
    const [adminUsernameChanges, setAdminUsernameChanges] = useState({});
    const [adminEmailChanges, setAdminEmailChanges] = useState({});
    const [holidays, setHolidays] = useState([]);
    const [newHoliday, setNewHoliday] = useState({date: '', description: ''});
    const [activeTab, setActiveTab] = useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('tab') || 'password';
    });

    const [orgDetails, setOrgDetails] = useState({
        name: '', address: '', phone: '', email: '', website: '',
        vat_id: '', iban: '', bic: '', bank_name: '', blz: '', account_number: '',
    });
    const [orgDetailsSaving, setOrgDetailsSaving] = useState(false);
    const [orgDetailsMsg, setOrgDetailsMsg] = useState({ text: '', ok: true });

    // Held as strings: an empty field has to stay distinguishable from a typed 0 until
    // it is parsed on save.
    const [revenueTargets, setRevenueTargets] = useState({});
    const [targetsSaving, setTargetsSaving] = useState(false);
    const [targetsMsg, setTargetsMsg] = useState({ text: '', ok: true });
    // The PUT replaces the whole map, so saving before the GET lands clears every year.
    const [targetsLoaded, setTargetsLoaded] = useState(false);
    // Last year for a correction, this year, next year for planning. A stored year
    // outside the window is left untouched rather than dropped on save.
    const targetYears = useMemo(() => {
        const y = new Date().getFullYear();
        return [y - 1, y, y + 1];
    }, []);

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoSuccess, setLogoSuccess] = useState('');
    const [logoError, setLogoError] = useState('');
    const logoInputRef = useRef(null);

    // The negative version, for documents that draw the logo on a dark band. Its own state rather
    // than a flag: both slots can be filled, and the previews sit side by side.
    const [darkLogoFile, setDarkLogoFile] = useState(null);
    const [darkLogoPreview, setDarkLogoPreview] = useState(null);
    const [darkLogoUploading, setDarkLogoUploading] = useState(false);
    const [darkLogoMsg, setDarkLogoMsg] = useState({ text: '', ok: true });
    const darkLogoInputRef = useRef(null);

    const [brandingColors, setBrandingColors] = useState({ primary_color: '#2563EB', dark_color: '#1e293b', header_text_color: '#FFFFFF' });
    const [brandingSaving, setBrandingSaving] = useState(false);
    const [brandingMsg, setBrandingMsg] = useState({ text: '', ok: true });
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterType, setFilterType] = useState('all');

    const [workingTimes, setWorkingTimes] = useState([]);
    const [newWorkingTime, setNewWorkingTime] = useState({
        userId: '',
        validFrom: '',
        validUntil: '',
        weekday: '1',
        expectedHours: '08:00'
    });
    const [selectedUserForWT, setSelectedUserForWT] = useState('all');

    const [appActions, setAppActions]               = useState([]);
    const [actionTypes, setActionTypes]             = useState([]);
    const [appActionsLoading, setAppActionsLoading] = useState(false);
    const [appActionsError, setAppActionsError]     = useState(null);
    const [actionFilterUser, setActionFilterUser]   = useState('');
    const [actionFilterType, setActionFilterType]   = useState('');
    const [actionFilterFrom, setActionFilterFrom]   = useState('');
    const [actionFilterTo, setActionFilterTo]       = useState('');
    const [actionSort, setActionSort]               = useState({ by: 'action_time', order: 'desc' });
    const [actionLimit, setActionLimit]             = useState(200);

    useEffect(() => {
        const currentRole = getCurrentUserRole();
        setRole(currentRole);

        getMe().then(me => setOwnProfile({ username: me.username || '', email: me.email || '' })).catch(() => {});

        if (currentRole === 'admin' || currentRole === 'super_admin') {
            void loadUsers();
            void loadHolidays();
            void loadWorkingTimes();
            getActionTypes().then(setActionTypes).catch(() => {});
        }
    }, []);

    const loadHolidays = async () => {
        try {
            const publicHoliday = await getPublicHolidays();
            setHolidays(publicHoliday);
        } catch (err) {
            console.error("Failed to load holidays", err);
        }
    };

    const loadUsers = async () => {
        try {
            const users = await getAllUsers();
            setUsers(users);
        } catch (err) {
            console.error("Failed to load users", err);
        }
    };

    const loadWorkingTimes = async () => {
        try {
            const wts = await getAllWorkingTime();
            setWorkingTimes(wts);
        } catch (err) {
            console.error("Failed to load working times", err);
        }
    };

    const fetchActions = useCallback(() => {
        setAppActionsLoading(true);
        setAppActionsError(null);
        getAppActions({
            user_id:     actionFilterUser || undefined,
            action_type: actionFilterType || undefined,
            start_date:  actionFilterFrom || undefined,
            end_date:    actionFilterTo   || undefined,
            sort_by:     actionSort.by,
            sort_order:  actionSort.order,
            limit:       actionLimit,
        })
            .then(setAppActions)
            .catch(() => setAppActionsError('Fehler beim Laden der App-Aktionen.'))
            .finally(() => setAppActionsLoading(false));
    }, [actionFilterUser, actionFilterType, actionFilterFrom, actionFilterTo, actionSort, actionLimit]);

    const filteredHolidays = holidays
        .filter(h => new Date(h.date).getFullYear() === parseInt(filterYear))
        .filter(h => {
            const today = new Date();
            const holidayDate = new Date(h.date);
            if (filterType === 'future') return holidayDate >= today;
            if (filterType === 'past') return holidayDate < today;
            return true;
        });

    const handleDeleteUser = async (userId) => {
        if (!await confirm("Benutzer wirklich löschen?", { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteUser(userId);
            await loadUsers();
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Löschen des Benutzers"));
        }
    };

    const handleToggleUserActive = async (user) => {
        const nextIsActive = !user.is_active;

        if (!await confirm(`Benutzer "${user.username}" wirklich ${nextIsActive ? 'aktivieren' : 'deaktivieren'}?`, { confirmLabel: 'Bestätigen', danger: false })) return;

        try {
            await changeUserActiveState(user.id, nextIsActive);
            await loadUsers();
        } catch (err) {
            console.error("Error changing user active state", err);
            toast(extractApiError(err, "Fehler beim Ändern des Aktiv-Status"));
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateUser(userId, { role: newRole });
            await loadUsers();
            toast(`Rolle geändert zu ${newRole === 'admin' ? 'Admin' : 'User'}.`, 'success');
        } catch (err) {
            toast(extractApiError(err, 'Fehler beim Ändern der Rolle.'));
        }
    };

    const handleOwnPasswordChange = async () => {
        const userId = getCurrentUserId();
        if (!currentPassword) { toast("Bitte aktuelles Passwort eingeben."); return; }
        if (!ownPassword) { toast("Bitte neues Passwort eingeben."); return; }
        if (ownPassword.length < 8) { toast("Passwort muss mindestens 8 Zeichen lang sein."); return; }

        try {
            await changePassword(userId, ownPassword, currentPassword);
            setOwnPassword('');
            setCurrentPassword('');
            toast("Passwort geändert");
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Ändern des Passworts"));
        }
    };

    const handleOwnProfileUpdate = async () => {
        const userId = getCurrentUserId();
        try {
            await updateProfile(userId, {
                username: ownProfile.username || undefined,
                email: ownProfile.email || undefined,
            });
            toast("Profil gespeichert");
        } catch (err) {
            const msg = err.response?.data?.detail;
            if (msg === 'Username already taken') {
                toast("Benutzername bereits vergeben.");
            } else {
                toast(extractApiError(err, "Fehler beim Speichern des Profils"));
            }
        }
    };

    const handleAdminPasswordChange = async (userId) => {
        const newPassword = adminPasswordChanges[userId];
        if (!newPassword) { toast("Bitte neues Passwort eingeben."); return; }

        try {
            await changePassword(userId, newPassword);
            setAdminPasswordChanges(prev => ({...prev, [userId]: ''}));
            toast("Passwort geändert");
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Ändern des Passworts"));
        }
    };

    const handleAdminUserUpdate = async (userId) => {
        const newUsername = adminUsernameChanges[userId];
        const newPassword = adminPasswordChanges[userId];
        const newEmail = adminEmailChanges[userId];
        if (!newUsername && !newPassword && !newEmail) { toast("Mindestens ein Feld ausfüllen"); return; }

        try {
            const dto = {};
            if (newUsername) dto.username = newUsername;
            if (newPassword) dto.password_clear = newPassword;
            if (newEmail) dto.email = newEmail;
            await updateUser(userId, dto);
            setAdminUsernameChanges(prev => ({ ...prev, [userId]: '' }));
            setAdminPasswordChanges(prev => ({ ...prev, [userId]: '' }));
            setAdminEmailChanges(prev => ({ ...prev, [userId]: '' }));
            await loadUsers();
            toast("Benutzer aktualisiert");
        } catch (err) {
            const msg = err.response?.data?.detail;
            if (msg === 'Username already taken') {
                toast("Benutzername bereits vergeben.");
            } else {
                toast(extractApiError(err, "Fehler beim Aktualisieren"));
            }
        }
    };

    const handleCreateUser = async () => {
        if (!newUser.username || !newUser.passwordClear || !newUser.name || !newUser.email) {
            toast("Bitte alle Felder ausfüllen.");
            return;
        }

        try {
            await addUser(newUser);
            setNewUser({
                username: '',
                passwordClear: '',
                email: '',
                role: 'user',
                name: ''
            });
            await loadUsers();
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Erstellen des Benutzers"));
        }
    };

    const handleAddHoliday = async () => {
        if (!newHoliday.date || !newHoliday.description) {
            toast("Bitte alle Felder ausfüllen.");
            return;
        }

        try {
            await addPublicHoliday(newHoliday);
            setNewHoliday({date: '', description: ''});
            await loadHolidays();
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Hinzufügen des Feiertags"));
        }
    };

    const handleDeleteHoliday = async (id) => {
        if (!await confirm("Feiertag wirklich löschen?", { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deletePublicHoliday(id);
            await loadHolidays();
        } catch (err) {
            toast(extractApiError(err, "Fehler beim Löschen des Feiertags"));
        }
    };

    const weekdayToLabel = (weekday) => {
        // Backend convention: 1=Montag … 7=Sonntag
        const labels = {
            1: 'Montag',
            2: 'Dienstag',
            3: 'Mittwoch',
            4: 'Donnerstag',
            5: 'Freitag',
            6: 'Samstag',
            7: 'Sonntag',
        };
        return labels[weekday] ?? weekday;
    };

    const findUserName = (userId) => {
        const u = users.find(u => u.id === userId);
        return u ? `${u.username} (${u.name})` : userId;
    };

    const filteredWorkingTimes = workingTimes.filter(wt =>
        selectedUserForWT === 'all' ? true : wt.userId === selectedUserForWT
    );

    const handleAddWorkingTime = async () => {
        const {userId, validFrom, validUntil, weekday, expectedHours} = newWorkingTime;

        if (!userId || !validFrom || !expectedHours) {
            toast("Bitte Benutzer, Gültig ab und Stunden ausfüllen");
            return;
        }

        try {
            const [h, m] = expectedHours.split(':');
            const expected = `${h.padStart(2, '0')}:${(m || '00').padStart(2, '0')}:00`;

            await addWorkingTime({
                user_id: userId,
                valid_from: validFrom,
                valid_until: validUntil || null,
                weekday: parseInt(weekday, 10),
                expected_hours: expected,
            });

            setNewWorkingTime({
                userId: '',
                validFrom: '',
                validUntil: '',
                weekday: '1',
                expectedHours: '08:00'
            });

            await loadWorkingTimes();
        } catch (err) {
            console.error("Error adding working time", err);
            toast(extractApiError(err, "Fehler beim Anlegen der Arbeitszeit-Regel"));
        }
    };

    const handleDeleteWorkingTime = async (id) => {
        if (!await confirm("Diese Arbeitszeit-Regel löschen?", { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteWorkingTime(id);
            await loadWorkingTimes();
        } catch (err) {
            console.error("Error deleting working time", err);
            toast(extractApiError(err, "Fehler beim Löschen der Arbeitszeit-Regel"));
        }
    };

    const handleActionSort = (col) => {
        setActionSort(prev =>
            prev.by === col
                ? { by: col, order: prev.order === 'desc' ? 'asc' : 'desc' }
                : { by: col, order: 'desc' }
        );
    };

    const resetActionFilters = () => {
        setActionFilterUser('');
        setActionFilterType('');
        setActionFilterFrom('');
        setActionFilterTo('');
        setActionSort({ by: 'action_time', order: 'desc' });
        setActionLimit(200);
    };

    const actionThProps = (col) => ({
        className: `sortable${actionSort.by === col ? ' active' : ''}`,
        onClick: () => handleActionSort(col),
    });

    const holidayCount = filteredHolidays.length;

    useEffect(() => {
        // `isAdmin`, not `role !== 'admin'`: the section below renders for a super_admin
        // too, and PATCH /details writes every column.
        if (activeTab !== 'firma' || !isAdmin) return;
        // Before the org-id guard below: this endpoint takes none, and a super_admin's
        // token carries no organisation_id.
        setTargetsLoaded(false);
        getRevenueTargets()
            .then(r => {
                setRevenueTargets(
                    Object.fromEntries(Object.entries(r?.targets || {}).map(([y, v]) => [y, String(v)])),
                );
                setTargetsLoaded(true);
            })
            .catch(() => setTargetsMsg({
                text: 'Umsatzziele konnten nicht geladen werden.', ok: false,
            }));
        const orgId = getCurrentUserOrgId();
        if (!orgId) return;
        api.get(`/Organisation/${orgId}/branding`)
            .then(r => setBrandingColors(r.data))
            .catch(() => {});
        api.get(`/Organisation/${orgId}/details`)
            .then(r => setOrgDetails(r.data))
            .catch(() => {});
        api.get(`/Organisation/${orgId}/logo`, { responseType: 'blob' })
            .then(r => setLogoPreview(URL.createObjectURL(r.data)))
            .catch(() => {});
        // A 404 here is the normal state — most organisations have no negative version —
        // so it clears the preview instead of surfacing an error.
        api.get(`/Organisation/${orgId}/logo-dark`, { responseType: 'blob' })
            .then(r => setDarkLogoPreview(URL.createObjectURL(r.data)))
            .catch(() => setDarkLogoPreview(null));
    }, [activeTab, isAdmin]);

    useEffect(() => {
        if (activeTab === 'app_actions' && isAdmin) {
            fetchActions();
        }
    }, [activeTab, role, fetchActions]);

    const handleBrandingSave = async () => {
        const orgId = getCurrentUserOrgId();
        if (!orgId) return;
        setBrandingSaving(true);
        setBrandingMsg({ text: '', ok: true });
        try {
            await api.patch(`/Organisation/${orgId}/branding`, brandingColors);
            applyBranding(brandingColors);
            setBrandingMsg({ text: 'Farben gespeichert.', ok: true });
        } catch (err) {
            setBrandingMsg({ text: err.response?.data?.detail || 'Fehler beim Speichern.', ok: false });
        } finally {
            setBrandingSaving(false);
        }
    };

    const handleOrgDetailsSave = async () => {
        const orgId = getCurrentUserOrgId();
        if (!orgId) return;
        setOrgDetailsSaving(true);
        setOrgDetailsMsg({ text: '', ok: true });
        try {
            await api.patch(`/Organisation/${orgId}/details`, orgDetails);
            setOrgDetailsMsg({ text: 'Firmendaten gespeichert.', ok: true });
        } catch (err) {
            setOrgDetailsMsg({ text: err.response?.data?.detail || 'Fehler beim Speichern.', ok: false });
        } finally {
            setOrgDetailsSaving(false);
        }
    };

    const handleTargetsSave = async () => {
        // Guarded, not merely disabled: the PUT is a wholesale replace.
        if (!targetsLoaded) {
            setTargetsMsg({ text: 'Umsatzziele wurden nicht geladen — nicht gespeichert.', ok: false });
            return;
        }
        setTargetsSaving(true);
        setTargetsMsg({ text: '', ok: true });
        // Like the FX fields in InvoiceModal: an emptied field means "no target", not 0.
        const payload = {};
        for (const [year, raw] of Object.entries(revenueTargets)) {
            const value = parseFloat(String(raw).replace(',', '.'));
            if (Number.isFinite(value) && value > 0) payload[year] = value;
        }
        try {
            await saveRevenueTargets(payload);
            setTargetsMsg({ text: 'Umsatzziele gespeichert.', ok: true });
        } catch (err) {
            setTargetsMsg({ text: err.response?.data?.detail || 'Fehler beim Speichern.', ok: false });
        } finally {
            setTargetsSaving(false);
        }
    };

    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        setLogoSuccess('');
        setLogoError('');
    };

    const handleLogoUpload = async () => {
        if (!logoFile) return;
        const orgId = getCurrentUserOrgId();
        if (!orgId) return setLogoError('Keine Organisation gefunden.');
        setLogoUploading(true);
        setLogoSuccess('');
        setLogoError('');
        try {
            const formData = new FormData();
            formData.append('file', logoFile);
            await api.post(`/Organisation/${orgId}/logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setLogoSuccess('Logo erfolgreich hochgeladen.');
            setLogoFile(null);
            api.get(`/Organisation/${orgId}/logo`, { responseType: 'blob' })
                .then(r => setLogoPreview(URL.createObjectURL(r.data)))
                .catch(() => {});
        } catch (err) {
            setLogoError(err.response?.data?.detail || 'Fehler beim Hochladen.');
        } finally {
            setLogoUploading(false);
        }
    };

    const handleDarkLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setDarkLogoFile(file);
        setDarkLogoPreview(URL.createObjectURL(file));
        setDarkLogoMsg({ text: '', ok: true });
    };

    const handleDarkLogoUpload = async () => {
        if (!darkLogoFile) return;
        const orgId = getCurrentUserOrgId();
        if (!orgId) return setDarkLogoMsg({ text: 'Keine Organisation gefunden.', ok: false });
        setDarkLogoUploading(true);
        setDarkLogoMsg({ text: '', ok: true });
        try {
            const formData = new FormData();
            formData.append('file', darkLogoFile);
            await api.post(`/Organisation/${orgId}/logo-dark`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setDarkLogoMsg({ text: 'Logo für dunkle Flächen hochgeladen.', ok: true });
            setDarkLogoFile(null);
            api.get(`/Organisation/${orgId}/logo-dark`, { responseType: 'blob' })
                .then(r => setDarkLogoPreview(URL.createObjectURL(r.data)))
                .catch(() => {});
        } catch (err) {
            setDarkLogoMsg({ text: extractApiError(err, 'Fehler beim Hochladen.'), ok: false });
        } finally {
            setDarkLogoUploading(false);
        }
    };

    // Removing it is a real delete, not a cleared preview: an empty slot is what puts the
    // CV and the report back on the ordinary logo.
    const handleDarkLogoDelete = async () => {
        const orgId = getCurrentUserOrgId();
        if (!orgId) return;
        if (!await confirm(
            'Logo für dunkle Flächen entfernen? CV und Projektbericht verwenden danach wieder das normale Logo.',
            { confirmLabel: 'Entfernen', danger: true },
        )) return;
        setDarkLogoUploading(true);
        try {
            await api.delete(`/Organisation/${orgId}/logo-dark`);
            setDarkLogoFile(null);
            setDarkLogoPreview(null);
            setDarkLogoMsg({ text: 'Entfernt. Es gilt wieder das normale Logo.', ok: true });
        } catch (err) {
            setDarkLogoMsg({ text: extractApiError(err, 'Fehler beim Entfernen.'), ok: false });
        } finally {
            setDarkLogoUploading(false);
        }
    };

    const holidayCountLabel =
        filterType === 'future'
            ? 'Zukünftige Feiertage'
            : filterType === 'past'
                ? 'Vergangene Feiertage'
                : 'Feiertage';

    const holidayFilterSummary = `${holidayCountLabel} ${filterYear}: ${holidayCount}`;

    return (
        <div className="settings-container">
            <h2>Einstellungen</h2>

            <div className="internal-nav">
                <button
                    className={`internal-tab ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => setActiveTab('password')}>
                    Mein Profil
                </button>
                <button
                    className={`internal-tab ${activeTab === 'appearance' ? 'active' : ''}`}
                    onClick={() => setActiveTab('appearance')}>
                    Erscheinungsbild
                </button>
                {isAdmin && (
                    <>
                        <button
                            className={`internal-tab ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => setActiveTab('users')}>
                            Benutzerverwaltung
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'holidays' ? 'active' : ''}`}
                            onClick={() => setActiveTab('holidays')}>
                            Feiertage
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'closures' ? 'active' : ''}`}
                            onClick={() => setActiveTab('closures')}>
                            Betriebsschließung
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'working_time' ? 'active' : ''}`}
                            onClick={() => setActiveTab('working_time')}>
                            Arbeitszeit
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'notifications' ? 'active' : ''}`}
                            onClick={() => setActiveTab('notifications')}>
                            Benachrichtigungen
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'firma' ? 'active' : ''}`}
                            onClick={() => setActiveTab('firma')}>
                            Firma
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'app_actions' ? 'active' : ''}`}
                            onClick={() => setActiveTab('app_actions')}>
                            App-Aktionen
                        </button>
                        <button
                            className={`internal-tab ${activeTab === 'export' ? 'active' : ''}`}
                            onClick={() => setActiveTab('export')}>
                            Datenexport
                        </button>
                    </>
                )}
            </div>

            {activeTab === 'password' && (
                <>
                    <div className="own-password-section">
                        <h3>Mein Profil</h3>

                        <label className="settings-field-label">Benutzername</label>
                        <input
                            type="text"
                            value={ownProfile.username}
                            onChange={e => setOwnProfile(p => ({ ...p, username: e.target.value }))}
                            className="input"
                            placeholder="Benutzername"
                        />

                        <label className="settings-field-label">E-Mail</label>
                        <input
                            type="email"
                            value={ownProfile.email}
                            onChange={e => setOwnProfile(p => ({ ...p, email: e.target.value }))}
                            className="input"
                            placeholder="E-Mail-Adresse"
                        />

                        <button onClick={handleOwnProfileUpdate} className="button" style={{ marginTop: 8 }}>
                            Profil speichern
                        </button>

                        <h3 style={{ marginTop: 24 }}>Passwort ändern</h3>
                        <label className="settings-field-label">Aktuelles Passwort</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={e => setCurrentPassword(e.target.value)}
                            className="input"
                            placeholder="Aktuelles Passwort"
                            autoComplete="current-password"
                        />
                        <label className="settings-field-label" style={{ marginTop: 8 }}>Neues Passwort</label>
                        <input
                            type="password"
                            value={ownPassword}
                            onChange={e => setOwnPassword(e.target.value)}
                            className="input"
                            placeholder="Neues Passwort (min. 8 Zeichen)"
                            autoComplete="new-password"
                        />
                        <br/>
                        <button onClick={handleOwnPasswordChange} className="button">Passwort ändern</button>
                    </div>

                </>
            )}

            {activeTab === 'appearance' && (
                <div className="appearance-section">
                    <h3>Erscheinungsbild</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                        Wähle das Farbschema der Anwendung.
                    </p>
                    <div className="appearance-toggle">
                        <button
                            className={`appearance-btn${colorScheme === 'system' ? ' active' : ''}`}
                            onClick={() => setColorScheme('system')}
                        >
                            System
                        </button>
                        <button
                            className={`appearance-btn${colorScheme === 'light' ? ' active' : ''}`}
                            onClick={() => setColorScheme('light')}
                        >
                            Hell
                        </button>
                        <button
                            className={`appearance-btn${colorScheme === 'dark' ? ' active' : ''}`}
                            onClick={() => setColorScheme('dark')}
                        >
                            Dunkel
                        </button>
                    </div>
                </div>
            )}

            {isAdmin && activeTab === 'working_time' && (
                <div className="admin-section">
                    <h3>Arbeitszeit-Regeln verwalten</h3>

                    <div className="two-col-grid">
                        <div className="two-col-left">
                            <h4>Neue Arbeitszeit-Regel anlegen</h4>

                            <div className="workingtime-form">
                                <label>Benutzer</label>
                                <select
                                    className="input"
                                    value={newWorkingTime.userId}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, userId: e.target.value}))
                                    }
                                >
                                    <option value="">– Benutzer wählen –</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.username} ({u.name})
                                        </option>
                                    ))}
                                </select>

                                <label>Gültig ab</label>
                                <input
                                    type="date"
                                    className="input"
                                    value={newWorkingTime.validFrom}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, validFrom: e.target.value}))
                                    }
                                />

                                <label>Gültig bis (optional)</label>
                                <input
                                    type="date"
                                    className="input"
                                    value={newWorkingTime.validUntil}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, validUntil: e.target.value}))
                                    }
                                />

                                <label>Wochentag</label>
                                <select
                                    className="input"
                                    value={newWorkingTime.weekday}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, weekday: e.target.value}))
                                    }
                                >
                                    <option value="1">Montag</option>
                                    <option value="2">Dienstag</option>
                                    <option value="3">Mittwoch</option>
                                    <option value="4">Donnerstag</option>
                                    <option value="5">Freitag</option>
                                    <option value="6">Samstag</option>
                                    <option value="7">Sonntag</option>
                                </select>

                                <label>Erwartete Stunden (HH:mm)</label>
                                <input
                                    type="time"
                                    className="input"
                                    value={newWorkingTime.expectedHours}
                                    onChange={(e) =>
                                        setNewWorkingTime((prev) => ({...prev, expectedHours: e.target.value}))
                                    }
                                />

                                <button onClick={handleAddWorkingTime} className="button" style={{marginTop: 8}}>
                                    Regel anlegen
                                </button>
                            </div>
                        </div>

                        <div className="two-col-right">
                            <h4>Bestehende Regeln</h4>

                            <div className="table-toolbar">
                                <div>
                                    <label>Filter nach Benutzer:</label>
                                    <select
                                        className="input"
                                        value={selectedUserForWT}
                                        onChange={(e) => setSelectedUserForWT(e.target.value)}
                                    >
                                        <option value="all">Alle</option>
                                        {users.map((u) => (
                                            <option key={u.id} value={u.id}>
                                                {u.username} ({u.name})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="working-time-list">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>Benutzer</th>
                                        <th>Wochentag</th>
                                        <th>Gültig ab</th>
                                        <th>Gültig bis</th>
                                        <th>Erwartete Stunden</th>
                                        <th>Aktion</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredWorkingTimes.map((wt) => (
                                        <tr key={wt.id}>
                                            <td>{findUserName(wt.userId)}</td>
                                            <td>{weekdayToLabel(wt.weekday)}</td>
                                            <td>{wt.validFrom ? formatDate(wt.validFrom) : "-"}</td>
                                            <td>{wt.validUntil ? formatDate(wt.validUntil) : "-"}</td>
                                            <td>{wt.expectedHours ? wt.expectedHours.slice(0, 5) : "-"}</td>
                                            <td>
                                                <button
                                                    className="button button-danger"
                                                    onClick={() => handleDeleteWorkingTime(wt.id)}
                                                >
                                                    Löschen
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredWorkingTimes.length === 0 && (
                                        <tr>
                                            <td colSpan={6}>Keine Arbeitszeit-Regeln gefunden.</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isAdmin && activeTab === 'users' && (
                <div className="admin-section">
                    <h3>Benutzer Management</h3>

                    <div className="two-col-grid">
                        <div className="two-col-left">
                            <h4>Neuer Benutzer anlegen</h4>

                            <input
                                placeholder="Benutzername"
                                value={newUser.username}
                                onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                                className="input"
                            />
                            <input
                                placeholder="Name"
                                value={newUser.name}
                                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                                className="input"
                            />
                            <input
                                placeholder="E-Mail"
                                type="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                className="input"
                            />
                            <input
                                placeholder="Passwort"
                                type="password"
                                value={newUser.passwordClear}
                                onChange={(e) => setNewUser({...newUser, passwordClear: e.target.value})}
                                className="input"
                            />
                            <select
                                value={newUser.role}
                                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                                className="input"
                            >
                                <option value="user">Mitarbeiter</option>
                                <option value="admin">Administrator</option>
                            </select>

                            <button onClick={handleCreateUser} className="button" style={{marginTop: 8}}>
                                Benutzer anlegen
                            </button>
                        </div>

                        <div className="two-col-right">
                            <h4>Mitarbeiter</h4>

                            <table className="table">
                                <thead>
                                <tr>
                                    <th>Benutzername</th>
                                    <th>E-Mail</th>
                                    <th>Rolle</th>
                                    <th>Status</th>
                                    {role === 'super_admin' && <th>Benutzername</th>}
                                    {role === 'super_admin' && <th>E-Mail</th>}
                                    {isAdmin && <th>Passwort</th>}
                                    <th>Aktion</th>
                                </tr>
                                </thead>
                                <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td>{user.username}</td>
                                        <td>{user.email || '—'}</td>
                                        <td>
                                            {role === 'admin' ? (
                                                <select
                                                    className="input"
                                                    value={user.role}
                                                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                    style={{padding: '3px 6px', height: 'auto', fontSize: 'var(--font-size-sm)'}}
                                                >
                                                    <option value="user">Mitarbeiter</option>
                                                    <option value="admin">Administrator</option>
                                                </select>
                                            ) : (
                                                { user: 'Mitarbeiter', admin: 'Administrator', super_admin: 'Superadmin' }[user.role] ?? user.role
                                            )}
                                        </td>

                                        <td>
                                            {user.is_active ? 'Aktiv' : 'Inaktiv'}
                                        </td>

                                        {role === 'super_admin' && (
                                            <td>
                                                <input
                                                    type="text"
                                                    value={adminUsernameChanges[user.id] ?? ''}
                                                    onChange={(e) =>
                                                        setAdminUsernameChanges((prev) => ({
                                                            ...prev,
                                                            [user.id]: e.target.value,
                                                        }))
                                                    }
                                                    className="input"
                                                    placeholder={user.username}
                                                />
                                            </td>
                                        )}

                                        {role === 'super_admin' && (
                                            <td>
                                                <input
                                                    type="email"
                                                    value={adminEmailChanges[user.id] ?? ''}
                                                    onChange={(e) =>
                                                        setAdminEmailChanges((prev) => ({
                                                            ...prev,
                                                            [user.id]: e.target.value,
                                                        }))
                                                    }
                                                    className="input"
                                                    placeholder={user.email || 'E-Mail'}
                                                />
                                            </td>
                                        )}

                                        {isAdmin && (
                                            <td>
                                                {/* An admin must not re-set a superadmin's password: that would
                                                    hand them the account. Only a superadmin may. */}
                                                {user.role === 'super_admin' && role !== 'super_admin' ? (
                                                    <span className="text-muted">—</span>
                                                ) : (
                                                    <input
                                                        type="password"
                                                        autoComplete="new-password"
                                                        value={adminPasswordChanges[user.id] || ''}
                                                        onChange={(e) =>
                                                            setAdminPasswordChanges((prev) => ({
                                                                ...prev,
                                                                [user.id]: e.target.value,
                                                            }))
                                                        }
                                                        className="input"
                                                        placeholder="Neues Passwort"
                                                        aria-label={`Neues Passwort für ${user.username}`}
                                                    />
                                                )}
                                            </td>
                                        )}

                                        <td className="user-actions-row">
                                            {role === 'super_admin' ? (
                                                <button
                                                    className="button"
                                                    onClick={() => handleAdminUserUpdate(user.id)}>
                                                    Ändern
                                                </button>
                                            ) : user.role !== 'super_admin' ? (
                                                <button
                                                    className="button"
                                                    onClick={() => handleAdminPasswordChange(user.id)}>
                                                    Passwort ändern
                                                </button>
                                            ) : null}

                                            <button
                                                className={user.is_active ? 'button-caution' : 'button-success'}
                                                onClick={() => handleToggleUserActive(user)}
                                            >
                                                {user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                            </button>

                                            <button
                                                className="button button-danger"
                                                onClick={() => handleDeleteUser(user.id)}>
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {users.length === 0 && (
                                    <tr>
                                        <td colSpan={role === 'super_admin' ? 8 : 6}>Keine Benutzer gefunden.</td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {isAdmin && activeTab === 'closures' && (
                <div className="admin-section">
                    <h3>Betriebsschließungstage</h3>
                    <CompanyClosures />
                </div>
            )}

            {isAdmin && activeTab === 'notifications' && (
                <div className="admin-section">
                    <h3>Mailbenachrichtigungen</h3>
                    <NotificationRecipients />
                </div>
            )}

            {isAdmin && activeTab === 'holidays' && (
                <div className="admin-section">
                    <h3>Feiertage verwalten</h3>

                    <div className="two-col-grid">
                        <div className="two-col-left">
                            <h4>Filter</h4>

                            <div className="holiday-filter">
                                <div className="form-group">
                                    <label>Jahr:</label>
                                    <input
                                        type="number"
                                        className="input"
                                        value={filterYear}
                                        onChange={(e) => setFilterYear(e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Typ:</label>
                                    <select
                                        className="input"
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                    >
                                        <option value="all">Alle</option>
                                        <option value="future">Zukünftig</option>
                                        <option value="past">Vergangen</option>
                                    </select>
                                </div>
                            </div>

                            <hr style={{margin: "16px 0"}}/>

                            <h4>Feiertag hinzufügen</h4>
                            <input
                                type="date"
                                value={newHoliday.date}
                                onChange={(e) =>
                                    setNewHoliday((prev) => ({...prev, date: e.target.value}))
                                }
                                className="input"
                            />
                            <input
                                type="text"
                                placeholder="Beschreibung"
                                value={newHoliday.description}
                                onChange={(e) =>
                                    setNewHoliday((prev) => ({...prev, description: e.target.value}))
                                }
                                className="input"
                            />
                            <button onClick={handleAddHoliday} className="button" style={{marginTop: 8}}>
                                Hinzufügen
                            </button>
                        </div>

                        <div className="two-col-right">
                            <h4>Alle Feiertage</h4>

                            <div style={{marginBottom: 8, fontWeight: 600}}>
                                {holidayFilterSummary}
                            </div>

                            <div className="holiday-list">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>Datum</th>
                                        <th>Beschreibung</th>
                                        <th>Aktion</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredHolidays.map((h) => (
                                        <tr key={h.id}>
                                            <td>{formatDate(h.date)}</td>
                                            <td>{h.description}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleDeleteHoliday(h.id)}
                                                    className="button button-danger"
                                                >
                                                    Löschen
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredHolidays.length === 0 && (
                                        <tr>
                                            <td colSpan={3}>Keine Feiertage gefunden.</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isAdmin && activeTab === 'firma' && (
                <div className="admin-section">
                    <h3>Firmendaten</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                        Diese Angaben erscheinen auf allen Rechnungen und Angeboten.
                    </p>

                    <div className="org-details-grid">
                        <div className="org-details-col">
                            <h4 style={{ marginBottom: 12 }}>Allgemein</h4>
                            {[
                                { key: 'name', label: 'Firmenname' },
                                { key: 'address', label: 'Adresse', multiline: true },
                                { key: 'phone', label: 'Telefon' },
                                { key: 'email', label: 'E-Mail' },
                                { key: 'website', label: 'Website' },
                                { key: 'vat_id', label: 'USt-IdNr.' },
                            ].map(({ key, label, multiline }) => (
                                <div key={key} className="org-details-field">
                                    <label className="org-details-label">{label}</label>
                                    {multiline ? (
                                        <textarea
                                            className="input org-details-textarea"
                                            value={orgDetails[key] || ''}
                                            onChange={e => setOrgDetails(prev => ({ ...prev, [key]: e.target.value }))}
                                            rows={3}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            className="input"
                                            value={orgDetails[key] || ''}
                                            onChange={e => setOrgDetails(prev => ({ ...prev, [key]: e.target.value }))}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="org-details-col">
                            <h4 style={{ marginBottom: 12 }}>Bankverbindung</h4>
                            {[
                                { key: 'bank_name', label: 'Bankname' },
                                { key: 'blz', label: 'BLZ' },
                                { key: 'account_number', label: 'Konto-Nr.' },
                                { key: 'iban', label: 'IBAN' },
                                { key: 'bic', label: 'BIC' },
                            ].map(({ key, label }) => (
                                <div key={key} className="org-details-field">
                                    <label className="org-details-label">{label}</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={orgDetails[key] || ''}
                                        onChange={e => setOrgDetails(prev => ({ ...prev, [key]: e.target.value }))}
                                        style={{ fontFamily: key === 'iban' || key === 'bic' || key === 'blz' ? 'monospace' : undefined }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, marginBottom: 4 }}>
                        <button
                            className="button"
                            onClick={handleOrgDetailsSave}
                            disabled={orgDetailsSaving}
                            style={{ background: 'var(--color-primary)', color: '#fff' }}
                        >
                            {orgDetailsSaving ? 'Wird gespeichert…' : 'Firmendaten speichern'}
                        </button>
                        {orgDetailsMsg.text && (
                            <span className={orgDetailsMsg.ok ? 'logo-status--success' : 'logo-status--error'}>
                                {orgDetailsMsg.text}
                            </span>
                        )}
                    </div>

                    <hr style={{ margin: '28px 0 20px' }} />

                    <h3>Umsatzziele</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                        Jahresziel für den Netto-Umsatz. Erscheint im Umsatzverlauf der
                        Finanzübersicht als Ziellinie mit Zielerreichung und Hochrechnung.
                        Ein leeres Feld bedeutet: kein Ziel für dieses Jahr.
                    </p>

                    <div className="org-details-grid">
                        <div className="org-details-col">
                            {targetYears.map(y => (
                                <div key={y} className="org-details-field">
                                    <label className="org-details-label">{`Ziel ${y} (€ netto)`}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1000"
                                        className="input"
                                        disabled={!targetsLoaded}
                                        value={revenueTargets[String(y)] ?? ''}
                                        onChange={e => setRevenueTargets(prev => ({
                                            ...prev, [String(y)]: e.target.value,
                                        }))}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="org-details-col" />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, marginBottom: 4 }}>
                        <button
                            className="button"
                            onClick={handleTargetsSave}
                            disabled={targetsSaving || !targetsLoaded}
                            style={{ background: 'var(--color-primary)', color: '#fff' }}
                        >
                            {targetsSaving ? 'Wird gespeichert…' : 'Umsatzziele speichern'}
                        </button>
                        {targetsMsg.text && (
                            <span className={targetsMsg.ok ? 'logo-status--success' : 'logo-status--error'}>
                                {targetsMsg.text}
                            </span>
                        )}
                    </div>

                    <hr style={{ margin: '28px 0 20px' }} />

                    <h3>Firmenlogo</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                        Das Logo wird auf allen Rechnungen und Angeboten oben links angezeigt.
                        Unterstützte Formate: PNG, JPEG, SVG, WebP.
                    </p>

                    <div className="logo-upload-area">
                        <div
                            className={`logo-dropzone ${logoPreview ? 'logo-dropzone--has-image' : ''}`}
                            onClick={() => logoInputRef.current?.click()}
                        >
                            {logoPreview ? (
                                <img src={logoPreview} alt="Logo Vorschau" className="logo-preview-img" />
                            ) : (
                                <div className="logo-dropzone-placeholder">
                                    <svg viewBox="0 0 48 48" fill="none" className="logo-upload-icon">
                                        <rect x="4" y="10" width="40" height="28" rx="3" stroke="currentColor" strokeWidth="2"/>
                                        <circle cx="16" cy="20" r="4" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M4 34l10-8 8 6 8-10 14 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Bild auswählen</span>
                                    <span className="logo-dropzone-hint">PNG, JPEG, SVG oder WebP</span>
                                </div>
                            )}
                        </div>

                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleLogoFileChange}
                        />

                        <div className="logo-upload-controls">
                            {logoFile && (
                                <span className="logo-filename">{logoFile.name}</span>
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    className="button"
                                    onClick={() => logoInputRef.current?.click()}
                                >
                                    {logoPreview ? 'Anderes Bild wählen' : 'Bild auswählen'}
                                </button>
                                {logoFile && (
                                    <button
                                        className="button"
                                        onClick={handleLogoUpload}
                                        disabled={logoUploading}
                                        style={{ background: 'var(--color-primary)', color: '#fff' }}
                                    >
                                        {logoUploading ? 'Wird hochgeladen…' : 'Logo hochladen'}
                                    </button>
                                )}
                                {logoPreview && (
                                    <button
                                        className="button button-danger"
                                        onClick={() => {
                                            setLogoFile(null);
                                            setLogoPreview(null);
                                            setLogoSuccess('');
                                            setLogoError('');
                                        }}
                                    >
                                        Entfernen
                                    </button>
                                )}
                            </div>
                            {logoSuccess && (
                                <div className="logo-status logo-status--success">{logoSuccess}</div>
                            )}
                            {logoError && (
                                <div className="logo-status logo-status--error">{logoError}</div>
                            )}
                        </div>
                    </div>

                    <h4 style={{ marginTop: 28 }}>Logo für dunkle Flächen</h4>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                        Der Mitarbeiter-CV und der Projektbericht zeichnen das Logo auf ein dunkles
                        Band. Ein Logo für weißes Papier ist dort kaum zu lesen — hinterlegen Sie
                        hier die helle bzw. negative Fassung. Ohne Eintrag wird das normale Logo
                        verwendet.
                    </p>

                    <div className="logo-upload-area">
                        {/* Previewed on the band's own colour, because that is the only background
                            on which "is this version readable?" can be answered. */}
                        <div
                            className={`logo-dropzone logo-dropzone--dark ${darkLogoPreview ? 'logo-dropzone--has-image' : ''}`}
                            style={{ background: brandingColors.dark_color || '#1e293b' }}
                            onClick={() => darkLogoInputRef.current?.click()}
                        >
                            {darkLogoPreview ? (
                                <img src={darkLogoPreview} alt="Logo für dunkle Flächen" className="logo-preview-img" />
                            ) : (
                                <div className="logo-dropzone-placeholder logo-dropzone-placeholder--dark">
                                    <span>Helle Fassung auswählen</span>
                                    <span className="logo-dropzone-hint">PNG, JPEG, SVG oder WebP</span>
                                </div>
                            )}
                        </div>

                        <input
                            ref={darkLogoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleDarkLogoFileChange}
                        />

                        <div className="logo-upload-controls">
                            {darkLogoFile && (
                                <span className="logo-filename">{darkLogoFile.name}</span>
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    className="button"
                                    onClick={() => darkLogoInputRef.current?.click()}
                                >
                                    {darkLogoPreview ? 'Anderes Bild wählen' : 'Bild auswählen'}
                                </button>
                                {darkLogoFile && (
                                    <button
                                        className="button"
                                        onClick={handleDarkLogoUpload}
                                        disabled={darkLogoUploading}
                                        style={{ background: 'var(--color-primary)', color: '#fff' }}
                                    >
                                        {darkLogoUploading ? 'Wird hochgeladen…' : 'Logo hochladen'}
                                    </button>
                                )}
                                {darkLogoPreview && !darkLogoFile && (
                                    <button
                                        className="button button-danger"
                                        onClick={handleDarkLogoDelete}
                                        disabled={darkLogoUploading}
                                    >
                                        Entfernen
                                    </button>
                                )}
                            </div>
                            {darkLogoMsg.text && (
                                <div className={darkLogoMsg.ok ? 'logo-status logo-status--success' : 'logo-status logo-status--error'}>
                                    {darkLogoMsg.text}
                                </div>
                            )}
                        </div>
                    </div>

                    <hr style={{ margin: '28px 0 20px' }} />

                    <h3>Corporate Identity Farben</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 20 }}>
                        Diese Farben werden in allen generierten PDF-Dokumenten verwendet.
                    </p>

                    <div className="branding-color-row">
                        <div className="branding-color-field">
                            <label className="branding-color-label">Primärfarbe (Kopfzeile, Tabelle)</label>
                            <div className="branding-color-input-wrap">
                                <input
                                    type="color"
                                    value={brandingColors.primary_color || '#2563EB'}
                                    onChange={e => setBrandingColors(prev => ({ ...prev, primary_color: e.target.value }))}
                                    className="branding-color-swatch"
                                />
                                <input
                                    type="text"
                                    value={brandingColors.primary_color || '#2563EB'}
                                    onChange={e => setBrandingColors(prev => ({ ...prev, primary_color: e.target.value }))}
                                    className="input branding-color-hex"
                                    maxLength={7}
                                    spellCheck={false}
                                />
                            </div>
                        </div>

                        <div className="branding-color-field">
                            <label className="branding-color-label">Fußzeilen-Farbe (dunkle Leiste)</label>
                            <div className="branding-color-input-wrap">
                                <input
                                    type="color"
                                    value={brandingColors.dark_color || '#1e293b'}
                                    onChange={e => setBrandingColors(prev => ({ ...prev, dark_color: e.target.value }))}
                                    className="branding-color-swatch"
                                />
                                <input
                                    type="text"
                                    value={brandingColors.dark_color || '#1e293b'}
                                    onChange={e => setBrandingColors(prev => ({ ...prev, dark_color: e.target.value }))}
                                    className="input branding-color-hex"
                                    maxLength={7}
                                    spellCheck={false}
                                />
                            </div>
                        </div>

                        <div className="branding-color-field">
                            <label className="branding-color-label">Tabellenkopf Textfarbe</label>
                            <div className="branding-color-input-wrap">
                                <input
                                    type="color"
                                    value={brandingColors.header_text_color || '#FFFFFF'}
                                    onChange={e => setBrandingColors(prev => ({ ...prev, header_text_color: e.target.value }))}
                                    className="branding-color-swatch"
                                />
                                <input
                                    type="text"
                                    value={brandingColors.header_text_color || '#FFFFFF'}
                                    onChange={e => setBrandingColors(prev => ({ ...prev, header_text_color: e.target.value }))}
                                    className="input branding-color-hex"
                                    maxLength={7}
                                    spellCheck={false}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="branding-preview" style={{ borderColor: brandingColors.primary_color || '#2563EB' }}>
                        <div
                            className="branding-preview-header"
                            style={{
                                background: brandingColors.primary_color || '#2563EB',
                                color: brandingColors.header_text_color || '#FFFFFF',
                            }}
                        >
                            Kopfzeile / Tabellenkopf
                        </div>
                        <div className="branding-preview-body">
                            Rechnungsinhalt &amp; Positionen
                        </div>
                        <div
                            className="branding-preview-footer"
                            style={{ background: brandingColors.dark_color || '#1e293b' }}
                        >
                            Fußzeile — Bankdaten &amp; Kontakt
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                        <button
                            className="button"
                            onClick={handleBrandingSave}
                            disabled={brandingSaving}
                            style={{ background: 'var(--color-primary)', color: '#fff' }}
                        >
                            {brandingSaving ? 'Wird gespeichert…' : 'Farben speichern'}
                        </button>
                        {brandingMsg.text && (
                            <span className={brandingMsg.ok ? 'logo-status--success' : 'logo-status--error'}>
                                {brandingMsg.text}
                            </span>
                        )}
                    </div>
                </div>
            )}
            {isAdmin && activeTab === 'export' && (
                <div className="admin-section">
                    <h3>Datenexport</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: 20 }}>
                        Exportiere Unternehmensdaten als CSV-Datei. Wähle einen Datensatz, die gewünschten Spalten und einen optionalen Zeitraum.
                    </p>
                    <DataExportPanel users={users} />
                </div>
            )}
            {isAdmin && activeTab === 'app_actions' && (
                <div className="admin-section">
                    <h3>App-Aktionen</h3>

                    <div className="aa-filters">
                        <div className="aa-filter-group">
                            <label>Benutzer</label>
                            <select value={actionFilterUser} onChange={e => setActionFilterUser(e.target.value)}>
                                <option value="">Alle Benutzer</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.name} ({u.username})</option>
                                ))}
                            </select>
                        </div>

                        <div className="aa-filter-group">
                            <label>Aktionstyp</label>
                            <select value={actionFilterType} onChange={e => setActionFilterType(e.target.value)}>
                                <option value="">Alle Typen</option>
                                {actionTypes.map(t => (
                                    <option key={t} value={t}>{ACTION_TYPE_LABELS[t] ?? t}</option>
                                ))}
                            </select>
                        </div>

                        <div className="aa-filter-group">
                            <label>Von</label>
                            <input type="date" value={actionFilterFrom} onChange={e => setActionFilterFrom(e.target.value)} />
                        </div>

                        <div className="aa-filter-group">
                            <label>Bis</label>
                            <input type="date" value={actionFilterTo} onChange={e => setActionFilterTo(e.target.value)} />
                        </div>

                        <div className="aa-filter-group">
                            <label>Max. Einträge</label>
                            <select value={actionLimit} onChange={e => setActionLimit(Number(e.target.value))}>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                                <option value={500}>500</option>
                                <option value={1000}>1000</option>
                            </select>
                        </div>

                        <button className="button button-secondary aa-filter-reset" onClick={resetActionFilters}>
                            Zurücksetzen
                        </button>
                    </div>

                    {!appActionsLoading && !appActionsError && (
                        <div className="aa-result-info">{appActions.length} Einträge gefunden</div>
                    )}

                    {appActionsError && <p style={{ color: 'var(--color-danger)' }}>{appActionsError}</p>}

                    <div className="aa-table-wrap">
                        <table className="aa-table">
                            <thead>
                                <tr>
                                    <th {...actionThProps('action_time')}>
                                        Zeit <SortIcon active={actionSort.by === 'action_time'} order={actionSort.order} />
                                    </th>
                                    <th {...actionThProps('user_name')}>
                                        Benutzer <SortIcon active={actionSort.by === 'user_name'} order={actionSort.order} />
                                    </th>
                                    <th {...actionThProps('action_type')}>
                                        Aktionstyp <SortIcon active={actionSort.by === 'action_type'} order={actionSort.order} />
                                    </th>
                                    <th {...actionThProps('description')}>
                                        Beschreibung <SortIcon active={actionSort.by === 'description'} order={actionSort.order} />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {appActionsLoading ? (
                                    <tr><td colSpan={4} className="aa-empty">Lädt…</td></tr>
                                ) : appActions.length === 0 ? (
                                    <tr><td colSpan={4} className="aa-empty">Keine Einträge gefunden.</td></tr>
                                ) : (
                                    appActions.map(a => (
                                        <tr key={a.id}>
                                            <td className="td-time">{formatActionDateTime(a.action_time)}</td>
                                            <td className="td-user">
                                                {a.user_name
                                                    ? <>{a.user_name}<br /><span style={{ color: 'var(--color-text-muted)', fontSize: '11px' }}>{a.username}</span></>
                                                    : <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                                                }
                                            </td>
                                            <td className="td-type">
                                                <span className="aa-type-badge" title={a.action_type}>{ACTION_TYPE_LABELS[a.action_type] ?? a.action_type ?? '—'}</span>
                                            </td>
                                            <td className="td-desc">{a.description ?? '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
